import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

interface Article {
  id: string;
  title: string;
  slug: string;
  category: string;
  excerpt: string | null;
  content: string | null;
  image_url: string | null;
  author: string | null;
  read_time: string | null;
  featured: boolean;
  created_at: string;
}

interface ArticleFormData {
  title: string;
  category: string;
  excerpt: string;
  content: string;
  image_url: string;
  author: string;
  read_time: string;
  featured: boolean;
}

const emptyForm: ArticleFormData = {
  title: '',
  category: 'company-news',
  excerpt: '',
  content: '',
  image_url: '',
  author: 'BEKAALI Communications',
  read_time: '5 min read',
  featured: false,
};

const categories = [
  { value: 'company-news', label: 'Company News' },
  { value: 'industry-insight', label: 'Industry Insight' },
  { value: 'partnerships', label: 'Partnerships' },
  { value: 'trade-updates', label: 'Trade Updates' },
  { value: 'case-studies', label: 'Case Studies' },
];

export default function AdminArticlesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ArticleFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase.from('articles').select('*').order('created_at', { ascending: false });

      if (search) query = query.ilike('title', `%${search}%`);
      if (filterCategory) query = query.eq('category', filterCategory);

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      setArticles((data || []) as Article[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [search, filterCategory]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (article: Article) => {
    setEditingId(article.id);
    setForm({
      title: article.title,
      category: article.category,
      excerpt: article.excerpt || '',
      content: article.content || '',
      image_url: article.image_url || '',
      author: article.author || '',
      read_time: article.read_time || '',
      featured: article.featured,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);

    const slug = form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const data = {
      title: form.title,
      slug: editingId ? undefined : slug,
      category: form.category,
      excerpt: form.excerpt || null,
      content: form.content || null,
      image_url: form.image_url || null,
      author: form.author || null,
      read_time: form.read_time || null,
      featured: form.featured,
    };

    try {
      let err;
      if (editingId) {
        const { error: updateErr } = await supabase.from('articles').update(data).eq('id', editingId);
        err = updateErr;
      } else {
        const { error: insertErr } = await supabase.from('articles').insert(data);
        err = insertErr;
      }
      if (err) throw err;
      setShowForm(false);
      fetchArticles();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: delErr } = await supabase.from('articles').delete().eq('id', id);
      if (delErr) throw delErr;
      setDeleteConfirm(null);
      fetchArticles();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const catLabel = (value: string) => categories.find((c) => c.value === value)?.label || value;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900">Articles</h1>
            <p className="text-sm text-foreground-500 mt-1">Manage news and blog content</p>
          </div>
          <button
            onClick={openNewForm}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            Add Article
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-900/40 p-4">
            <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                {editingId ? 'Edit Article' : 'Add New Article'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Title *</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer">
                      {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Read Time</label>
                    <input type="text" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Author</label>
                  <input type="text" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Excerpt</label>
                  <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Content</label>
                  <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Image URL</label>
                  <input type="text" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-500 cursor-pointer" />
                  <span className="text-sm text-foreground-700">Featured article</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-background-200">
                <button onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving || !form.title.trim()}
                  className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-900/40 p-4">
            <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-sm p-6">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">Delete Article?</h3>
              <p className="text-sm text-foreground-500 mb-5">This action cannot be undone. The article will be permanently removed.</p>
              <div className="flex items-center justify-end gap-3">
                <button onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 whitespace-nowrap cursor-pointer">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-red-600 whitespace-nowrap cursor-pointer">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Articles Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
        ) : (
          <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100">
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs">Title</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden md:table-cell">Category</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden lg:table-cell">Author</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden sm:table-cell">Date</th>
                    <th className="text-right py-3 px-4 font-medium text-foreground-600 text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-foreground-500 text-sm">
                        No articles found.
                      </td>
                    </tr>
                  ) : (
                    articles.map((article) => (
                      <tr key={article.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                        <td className="py-3 px-4">
                          <p className="font-medium text-foreground-900 truncate max-w-[250px]">{article.title}</p>
                          <p className="text-xs text-foreground-500 truncate max-w-[250px]">{article.excerpt || 'No excerpt'}</p>
                        </td>
                        <td className="py-3 px-4 text-foreground-600 hidden md:table-cell">
                          <span className="inline-flex items-center gap-1">
                            {article.featured && <i className="ri-star-fill text-accent-500 text-xs" />}
                            {catLabel(article.category)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-foreground-600 hidden lg:table-cell">{article.author || '-'}</td>
                        <td className="py-3 px-4 text-foreground-500 text-xs hidden sm:table-cell whitespace-nowrap">
                          {new Date(article.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEditForm(article)}
                              className="p-1.5 rounded-md hover:bg-background-100 text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
                              title="Edit">
                              <i className="ri-edit-line text-base" />
                            </button>
                            <button onClick={() => setDeleteConfirm(article.id)}
                              className="p-1.5 rounded-md hover:bg-red-50 text-foreground-500 hover:text-red-600 transition-colors cursor-pointer"
                              title="Delete">
                              <i className="ri-delete-bin-line text-base" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-background-200 text-xs text-foreground-500">
              {articles.length} article{articles.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}